import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  AlertTriangle,
  ArrowRight,
  BrandWhatsapp,
  Check,
  ClipboardData,
  ExternalLink,
  Eye,
  GasStation,
  Mail,
  Refresh,
  Search,
  ShieldCheck,
  X,
} from "tabler-icons-react";
import {
  AnomalyCategory,
  AnomalyEvent,
  AnomalySeverity,
  QuantitativeAnomalySignal,
} from "@captiva/shared-types";

import { db } from "../firebase";
import { PageHeader } from "../components/shared";
import { ResponsiveDataTable, ResponsiveDataTableColumn } from "../components/listings/responsive-data-table";

export interface AnomaliesScreenProps {
  companyId: string;
}

const SEVERITY_ORDER: Record<AnomalySeverity, number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

const SEVERITY_COLORS: Record<AnomalySeverity, string> = {
  CRITICAL: "red",
  HIGH: "orange",
  MEDIUM: "yellow",
  LOW: "blue",
};

const STATUS_COLORS: Record<AnomalyEvent["status"], string> = {
  OPEN: "red",
  ACKNOWLEDGED: "blue",
  DISMISSED: "gray",
};

const FUEL_COLLECTION_LABELS: Record<string, string> = {
  capturedRefuel: "Roadside Refuel",
  capturedFuelTankIssue: "Onsite Refuel",
  capturedTankLevelReading: "Tank Reading",
  capturedFuelTankTransfer: "Fuel Transfer",
  capturedBulkFuelReceived: "Bulk Delivery",
};

const FUEL_COLLECTION_ROUTES: Record<string, string> = {
  capturedRefuel: "/dashboard/capturedRefuel",
  capturedFuelTankIssue: "/dashboard/capturedFuelTankIssue",
  capturedTankLevelReading: "/dashboard/capturedTankLevelReading",
  capturedFuelTankTransfer: "/dashboard/capturedFuelTankTransfer",
  capturedBulkFuelReceived: "/dashboard/capturedBulkFuelReceived",
};

export function formatCategoryLabel(category: AnomalyCategory | string): string {
  if (!category) return "Operational Outlier";
  return String(category)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function parseTimestampMs(val: any): number {
  if (!val) return 0;
  if (typeof val === "object" && ("seconds" in val || "_seconds" in val)) {
    return ((val.seconds ?? val._seconds) || 0) * 1000;
  }
  if (typeof val?.toMillis === "function") {
    return val.toMillis();
  }
  const ms = new Date(val).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function formatAnomalyTimestamp(val: any): string {
  const ms = parseTimestampMs(val);
  if (!ms) return "—";
  const d = new Date(ms);
  return (
    d.toLocaleDateString("en-GB") +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

function toIsoDateOnly(val: any): string {
  const ms = parseTimestampMs(val);
  if (!ms) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function buildSourceNavigationPath(event: AnomalyEvent): string {
  const dateStr = toIsoDateOnly(event.createdAt);
  if (event.domain === "FLEX" || event.sourceCollection === "flexFormSubmissions") {
    const params = new URLSearchParams();
    if (event.templateId) params.set("templateId", event.templateId);
    if (event.sourceDocumentId) params.set("submissionId", event.sourceDocumentId);
    params.set("mode", "view");
    if (dateStr) {
      params.set("from", dateStr);
      params.set("to", dateStr);
    }
    return `/dashboard/form-template-submissions?${params.toString()}`;
  }

  const baseRoute =
    FUEL_COLLECTION_ROUTES[event.sourceCollection] || "/dashboard/capturedRefuel";
  const params = new URLSearchParams();
  if (event.sourceDocumentId) {
    params.set("entryId", event.sourceDocumentId);
    params.set("entryMode", "view");
  }
  if (dateStr) {
    params.set("from", dateStr);
    params.set("to", dateStr);
  }
  const queryString = params.toString();
  return queryString ? `${baseRoute}?${queryString}` : baseRoute;
}

export function AnomaliesScreen({ companyId }: AnomaliesScreenProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const controlSize = isMobile ? "md" : "sm";

  const [events, setEvents] = useState<AnomalyEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Filters
  const [domainFilter, setDomainFilter] = useState<string>("ALL");
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Drawer inspection state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const fetchAnomalies = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, "anomalyEvents"),
        where("companyId", "==", companyId)
      );
      const snapshot = await getDocs(q);
      const loaded: AnomalyEvent[] = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as AnomalyEvent),
        id: docSnap.id,
      }));
      loaded.sort(
        (a, b) => parseTimestampMs(b.createdAt) - parseTimestampMs(a.createdAt)
      );
      setEvents(loaded);
    } catch (err: any) {
      console.error("Failed to fetch anomaly events:", err);
      setError(err?.message || "Failed to load anomaly events.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  const handleStatusUpdate = async (
    eventId: string | undefined,
    newStatus: AnomalyEvent["status"]
  ) => {
    if (!eventId) return;
    setUpdatingId(eventId);
    try {
      const eventRef = doc(db, "anomalyEvents", eventId);
      await updateDoc(eventRef, { status: newStatus });
      setEvents((prev) =>
        prev.map((ev) => (ev.id === eventId ? { ...ev, status: newStatus } : ev))
      );
    } catch (err: any) {
      console.error("Failed to update anomaly status:", err);
      setError(err?.message || "Failed to update anomaly status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const categoryOptions = useMemo(() => {
    const distinct = Array.from(
      new Set(events.map((e) => e.category).filter(Boolean))
    );
    return distinct
      .sort()
      .map((cat) => ({ value: cat, label: formatCategoryLabel(cat) }));
  }, [events]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (domainFilter !== "ALL" && ev.domain !== domainFilter) return false;
      if (severityFilter && ev.severity !== severityFilter) return false;
      if (statusFilter !== "ALL" && (ev.status || "OPEN") !== statusFilter)
        return false;
      if (categoryFilter && ev.category !== categoryFilter) return false;

      if (searchQuery.trim()) {
        const needle = searchQuery.trim().toLowerCase();
        const haystack = [
          ev.headline,
          ev.explanation,
          ev.recommendedAction,
          ev.entityLabel,
          ev.entityKey,
          ev.assetName,
          ev.templateName,
          ev.submitterName,
          ev.category,
          ev.sourceCollection,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      return true;
    });
  }, [
    events,
    domainFilter,
    severityFilter,
    statusFilter,
    categoryFilter,
    searchQuery,
  ]);

  const sortedEvents = useMemo(() => {
    if (!sortColumn) return filteredEvents;
    const copy = [...filteredEvents];
    const dir = sortDirection === "asc" ? 1 : -1;

    copy.sort((a, b) => {
      if (sortColumn === "createdAt") {
        return (parseTimestampMs(a.createdAt) - parseTimestampMs(b.createdAt)) * dir;
      }
      if (sortColumn === "severity") {
        return (
          ((SEVERITY_ORDER[a.severity] || 0) - (SEVERITY_ORDER[b.severity] || 0)) *
          dir
        );
      }
      if (sortColumn === "domain") {
        return String(a.domain || "").localeCompare(String(b.domain || "")) * dir;
      }
      if (sortColumn === "entity") {
        const labelA = a.entityLabel || a.assetName || a.entityKey || "";
        const labelB = b.entityLabel || b.assetName || b.entityKey || "";
        return labelA.localeCompare(labelB) * dir;
      }
      if (sortColumn === "headline") {
        return String(a.headline || "").localeCompare(String(b.headline || "")) * dir;
      }
      if (sortColumn === "status") {
        return String(a.status || "OPEN").localeCompare(String(b.status || "OPEN")) * dir;
      }
      return 0;
    });

    return copy;
  }, [filteredEvents, sortColumn, sortDirection]);

  const handleSort = (colKey: string) => {
    if (sortColumn === colKey) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(colKey);
      setSortDirection("desc");
    }
  };

  const kpiStats = useMemo(() => {
    const openEvents = events.filter((e) => (e.status || "OPEN") === "OPEN");
    const criticalOrHighOpen = openEvents.filter(
      (e) => e.severity === "CRITICAL" || e.severity === "HIGH"
    );
    const fuelCount = events.filter((e) => e.domain === "FUEL").length;
    const flexCount = events.filter((e) => e.domain === "FLEX").length;

    return {
      total: events.length,
      openCount: openEvents.length,
      criticalOrHighCount: criticalOrHighOpen.length,
      fuelCount,
      flexCount,
    };
  }, [events]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  const hasActiveFilters =
    domainFilter !== "ALL" ||
    Boolean(severityFilter) ||
    statusFilter !== "ALL" ||
    Boolean(categoryFilter) ||
    Boolean(searchQuery.trim());

  const resetFilters = () => {
    setDomainFilter("ALL");
    setSeverityFilter(null);
    setStatusFilter("ALL");
    setCategoryFilter(null);
    setSearchQuery("");
  };

  const columns: ResponsiveDataTableColumn<AnomalyEvent>[] = [
    {
      key: "severity",
      label: "Severity",
      render: (ev, isMobileRow) => (
        <Stack spacing={4} align="flex-start">
          <Badge
            color={SEVERITY_COLORS[ev.severity] || "orange"}
            variant="filled"
            size={isMobileRow ? "xs" : "sm"}
          >
            {ev.severity}
          </Badge>
          {isMobileRow && (
            <Badge
              color={STATUS_COLORS[ev.status || "OPEN"] || "gray"}
              variant="dot"
              size="xs"
            >
              {ev.status || "OPEN"}
            </Badge>
          )}
        </Stack>
      ),
    },
    {
      key: "createdAt",
      label: "Detected",
      hideOnMobile: true,
      render: (ev) => (
        <Text size="xs" style={{ whiteSpace: "nowrap" }}>
          {formatAnomalyTimestamp(ev.createdAt)}
        </Text>
      ),
    },
    {
      key: "domain",
      label: "Source",
      hideOnMobile: true,
      render: (ev) => {
        const isFuel = ev.domain === "FUEL";
        const subLabel = isFuel
          ? FUEL_COLLECTION_LABELS[ev.sourceCollection] || ev.sourceCollection
          : ev.templateName || "Flex Form";
        return (
          <Group spacing={6} noWrap>
            <Badge
              size="xs"
              color={isFuel ? "cap-orange" : "cap-blue"}
              variant="light"
              leftSection={
                isFuel ? <GasStation size={11} /> : <ClipboardData size={11} />
              }
            >
              {ev.domain}
            </Badge>
            <Text size="xs" color="dimmed" style={{ maxWidth: 160 }} lineClamp={1}>
              {subLabel}
            </Text>
          </Group>
        );
      },
    },
    {
      key: "entity",
      label: "Asset / Entity",
      render: (ev, isMobileRow) => (
        <div style={{ minWidth: 0 }}>
          <Text size="sm" weight={600} lineClamp={isMobileRow ? 2 : 1}>
            {ev.entityLabel || ev.assetName || ev.entityKey || "—"}
          </Text>
          <Text size="xs" color="dimmed" lineClamp={isMobileRow ? 2 : 1}>
            {formatCategoryLabel(ev.category)}
            {isMobileRow ? ` • ${formatAnomalyTimestamp(ev.createdAt)}` : ""}
          </Text>
          {isMobileRow && (
            <Text size="xs" weight={500} mt={2} lineClamp={2} style={{ lineHeight: 1.35 }}>
              {ev.headline}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "headline",
      label: "AI Finding",
      hideOnMobile: true,
      render: (ev) => (
        <div style={{ maxWidth: 380 }}>
          <Text size="sm" weight={500} lineClamp={1}>
            {ev.headline}
          </Text>
          <Text size="xs" color="dimmed" lineClamp={1}>
            {ev.explanation}
          </Text>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      hideOnMobile: true,
      render: (ev) => {
        const st = ev.status || "OPEN";
        return (
          <Group spacing={6} noWrap>
            <Badge color={STATUS_COLORS[st] || "gray"} variant="dot" size="sm">
              {st}
            </Badge>
            {ev.notifiedChannels?.whatsapp && (
              <Tooltip label="WhatsApp alert sent">
                <ThemeIcon size="xs" color="green" variant="light">
                  <BrandWhatsapp size={12} />
                </ThemeIcon>
              </Tooltip>
            )}
            {ev.notifiedChannels?.email && (
              <Tooltip label="Email alert sent">
                <ThemeIcon size="xs" color="blue" variant="light">
                  <Mail size={12} />
                </ThemeIcon>
              </Tooltip>
            )}
          </Group>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="AI Operational Anomalies"
        subtitle="Real-time statistical outlier detection and AI contextual evaluations across Captiva Fuel and Captiva Flex"
        action={
          <Group spacing="xs">
            <Button
              variant="default"
              size={controlSize}
              style={{ minHeight: isMobile ? 44 : 36 }}
              leftIcon={<Refresh size={16} />}
              onClick={fetchAnomalies}
              loading={loading}
              aria-label="Refresh anomalies"
            >
              Refresh
            </Button>
          </Group>
        }
      />

      {error && (
        <Alert
          icon={<AlertTriangle size={16} />}
          title="Unable to load anomalies"
          color="red"
          mb="md"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* KPI Summary Cards */}
      <SimpleGrid
        cols={4}
        spacing={isMobile ? "xs" : "md"}
        mb="md"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "xs", cols: 2 },
        ]}
      >
        <Paper withBorder p={isMobile ? "xs" : "sm"} radius="md">
          <Group position="apart" noWrap>
            <div>
              <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                Open Anomalies
              </Text>
              <Text size="xl" weight={700} color={kpiStats.openCount > 0 ? "red" : "dark"}>
                {kpiStats.openCount}
              </Text>
            </div>
            <ThemeIcon
              color={kpiStats.openCount > 0 ? "red" : "green"}
              variant="light"
              size="lg"
              radius="md"
            >
              {kpiStats.openCount > 0 ? (
                <AlertTriangle size={20} />
              ) : (
                <ShieldCheck size={20} />
              )}
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper withBorder p={isMobile ? "xs" : "sm"} radius="md">
          <Group position="apart" noWrap>
            <div>
              <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                Critical / High (Open)
              </Text>
              <Text
                size="xl"
                weight={700}
                color={kpiStats.criticalOrHighCount > 0 ? "orange" : "dark"}
              >
                {kpiStats.criticalOrHighCount}
              </Text>
            </div>
            <Badge
              color={kpiStats.criticalOrHighCount > 0 ? "red" : "gray"}
              variant="light"
              size="xs"
              style={{ flexShrink: 0 }}
            >
              Priority
            </Badge>
          </Group>
        </Paper>

        <Paper withBorder p={isMobile ? "xs" : "sm"} radius="md">
          <Group position="apart" noWrap>
            <div>
              <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                Captiva Fuel
              </Text>
              <Text size="xl" weight={700}>
                {kpiStats.fuelCount}
              </Text>
            </div>
            <ThemeIcon color="cap-orange" variant="light" size="lg" radius="md">
              <GasStation size={20} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper withBorder p={isMobile ? "xs" : "sm"} radius="md">
          <Group position="apart" noWrap>
            <div>
              <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
                Captiva Flex
              </Text>
              <Text size="xl" weight={700}>
                {kpiStats.flexCount}
              </Text>
            </div>
            <ThemeIcon color="cap-blue" variant="light" size="lg" radius="md">
              <ClipboardData size={20} />
            </ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* Responsive Filter Toolbar */}
      <Paper withBorder p={isMobile ? "sm" : "xs"} radius="md" mb="md" style={{ backgroundColor: "#f8f9fa" }}>
        <Group spacing={isMobile ? "sm" : "xs"} align="center" style={{ flexWrap: "wrap" }}>
          <SegmentedControl
            size={controlSize}
            fullWidth={isMobile}
            value={domainFilter}
            onChange={setDomainFilter}
            data={[
              { label: "All Sources", value: "ALL" },
              { label: "Fuel", value: "FUEL" },
              { label: "Flex", value: "FLEX" },
            ]}
            styles={
              isMobile
                ? {
                    root: { width: "100%", minHeight: 44, alignItems: "center" },
                    control: { minHeight: 38 },
                    label: {
                      minHeight: 38,
                      padding: "6px 10px",
                      fontSize: 14,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  }
                : undefined
            }
          />

          <SegmentedControl
            size="sm"
            fullWidth={isMobile}
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { label: "All Statuses", value: "ALL" },
              { label: "Open", value: "OPEN" },
              { label: "Acknowledged", value: "ACKNOWLEDGED" },
              { label: "Dismissed", value: "DISMISSED" },
            ]}
            styles={
              isMobile
                ? {
                    root: { width: "100%", minHeight: 44, alignItems: "center" },
                    control: { minHeight: 38 },
                    label: {
                      minHeight: 38,
                      padding: "6px 6px",
                      fontSize: 13,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                    },
                  }
                : undefined
            }
          />

          <div
            style={
              isMobile
                ? {
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    width: "100%",
                  }
                : { display: "contents" }
            }
          >
            <Select
              size={controlSize}
              placeholder="All Severities"
              clearable
              value={severityFilter}
              onChange={setSeverityFilter}
              data={[
                { value: "CRITICAL", label: "Critical" },
                { value: "HIGH", label: "High" },
                { value: "MEDIUM", label: "Medium" },
                { value: "LOW", label: "Low" },
              ]}
              style={{ width: isMobile ? "100%" : 155 }}
              aria-label="Filter by severity"
            />

            <Select
              size={controlSize}
              placeholder="All Categories"
              clearable
              value={categoryFilter}
              onChange={setCategoryFilter}
              data={categoryOptions}
              style={{ width: isMobile ? "100%" : 200 }}
              aria-label="Filter by category"
            />
          </div>

          <TextInput
            size={controlSize}
            icon={<Search size={16} />}
            placeholder="Search asset, headline, or details..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{
              flex: 1,
              minWidth: isMobile ? "100%" : 200,
              width: isMobile ? "100%" : undefined,
            }}
            aria-label="Search anomalies"
            rightSection={
              searchQuery ? (
                <ActionIcon
                  size="sm"
                  color="gray"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </ActionIcon>
              ) : null
            }
          />

          {hasActiveFilters && (
            <Button
              variant={isMobile ? "outline" : "subtle"}
              color="gray"
              size={controlSize}
              fullWidth={isMobile}
              style={{ minHeight: isMobile ? 44 : 36 }}
              leftIcon={<X size={14} />}
              onClick={resetFilters}
            >
              Reset
            </Button>
          )}
        </Group>
      </Paper>

      {/* Main Content */}
      {loading ? (
        <Group position="center" py="xl">
          <Loader size="md" />
          <Text size="sm" color="dimmed">
            Loading operational anomalies...
          </Text>
        </Group>
      ) : sortedEvents.length === 0 ? (
        <Card withBorder radius="md" p="xl" style={{ textAlign: "center" }}>
          <Stack align="center" spacing="xs">
            <ThemeIcon size={48} radius="xl" color="green" variant="light">
              <ShieldCheck size={28} />
            </ThemeIcon>
            <Text weight={600} size="md">
              {events.length === 0
                ? "No operational anomalies detected"
                : "No anomalies match your current filters"}
            </Text>
            <Text size="xs" color="dimmed" style={{ maxWidth: 460 }}>
              {events.length === 0
                ? "When the AI Anomaly Judge flags consumption spikes, tank reconciliation discrepancies, or form submission outliers, they will appear here."
                : "Try clearing one or more filters above to view all recorded anomaly events."}
            </Text>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size={controlSize}
                style={{ minHeight: isMobile ? 44 : 36 }}
                mt="xs"
                onClick={resetFilters}
              >
                Clear filters
              </Button>
            )}
          </Stack>
        </Card>
      ) : (
        <ResponsiveDataTable
          elements={sortedEvents}
          columns={columns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          actionsLabel={isMobile ? "" : "Actions"}
          rowProps={(ev) => ({
            style: { cursor: "pointer" },
            onClick: () => setSelectedEventId(ev.id || null),
          })}
          actionsRender={(ev, isMobileRow) => {
            const currentStatus = ev.status || "OPEN";
            const btnSize = isMobileRow ? "lg" : "sm";
            const iconSize = isMobileRow ? 18 : 15;
            const touchStyle = isMobileRow ? { minWidth: 38, minHeight: 38 } : undefined;
            return (
              <Group spacing={isMobileRow ? 6 : 4} noWrap>
                <Tooltip label="Inspect anomaly details">
                  <ActionIcon
                    size={btnSize}
                    style={touchStyle}
                    variant="light"
                    color="cap-blue"
                    aria-label={`Inspect anomaly ${ev.headline}`}
                    onClick={() => setSelectedEventId(ev.id || null)}
                  >
                    <Eye size={iconSize} />
                  </ActionIcon>
                </Tooltip>
                {currentStatus === "OPEN" && (
                  <Tooltip label="Acknowledge anomaly">
                    <ActionIcon
                      size={btnSize}
                      style={touchStyle}
                      variant="light"
                      color="green"
                      loading={updatingId === ev.id}
                      aria-label={`Acknowledge anomaly ${ev.headline}`}
                      onClick={() => handleStatusUpdate(ev.id, "ACKNOWLEDGED")}
                    >
                      <Check size={iconSize} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {!isMobileRow && (
                  <Tooltip label="Open source record">
                    <ActionIcon
                      size={btnSize}
                      variant="default"
                      color="gray"
                      aria-label={`Open source record for ${ev.headline}`}
                      onClick={() => navigate(buildSourceNavigationPath(ev))}
                    >
                      <ExternalLink size={iconSize} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            );
          }}
        />
      )}

      {/* Detail Inspection Drawer */}
      <Drawer
        opened={Boolean(selectedEvent)}
        onClose={() => setSelectedEventId(null)}
        title={
          <Group spacing="xs">
            <AlertTriangle
              size={20}
              color={
                selectedEvent?.severity === "CRITICAL" ||
                selectedEvent?.severity === "HIGH"
                  ? "#e03131"
                  : "#f08c00"
              }
            />
            <Text weight={700} size="md">
              Anomaly Investigation
            </Text>
          </Group>
        }
        padding="lg"
        size={isMobile ? "100%" : "xl"}
        position="right"
      >
        {selectedEvent && (
          <ScrollArea style={{ height: "calc(100vh - 90px)" }} pr="xs">
            <Stack spacing="md">
              <Group position="apart" align="flex-start">
                <Group spacing="xs">
                  <Badge
                    color={SEVERITY_COLORS[selectedEvent.severity] || "orange"}
                    variant="filled"
                    size="md"
                  >
                    {selectedEvent.severity}
                  </Badge>
                  <Badge
                    color={
                      selectedEvent.domain === "FUEL" ? "cap-orange" : "cap-blue"
                    }
                    variant="light"
                    size="md"
                  >
                    {selectedEvent.domain}
                  </Badge>
                  <Badge variant="outline" color="gray" size="md">
                    {formatCategoryLabel(selectedEvent.category)}
                  </Badge>
                </Group>
                <Badge
                  color={STATUS_COLORS[selectedEvent.status || "OPEN"] || "gray"}
                  variant="dot"
                  size="md"
                >
                  {selectedEvent.status || "OPEN"}
                </Badge>
              </Group>

              <div>
                <Text weight={700} size="lg">
                  {selectedEvent.headline}
                </Text>
                <Text size="xs" color="dimmed" mt={2}>
                  Detected on {formatAnomalyTimestamp(selectedEvent.createdAt)} •
                  Confidence: {Math.round((selectedEvent.confidence || 0) * 100)}%
                </Text>
              </div>

              <Paper withBorder p="sm" radius="md" style={{ backgroundColor: "#f8f9fa" }}>
                <SimpleGrid cols={2} spacing="xs">
                  <div>
                    <Text size="xs" color="dimmed" weight={600}>
                      Asset / Entity
                    </Text>
                    <Text size="sm" weight={600}>
                      {selectedEvent.entityLabel ||
                        selectedEvent.assetName ||
                        selectedEvent.entityKey ||
                        "—"}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" color="dimmed" weight={600}>
                      Source Collection / Form
                    </Text>
                    <Text size="sm">
                      {selectedEvent.templateName ||
                        FUEL_COLLECTION_LABELS[selectedEvent.sourceCollection] ||
                        selectedEvent.sourceCollection}
                    </Text>
                  </div>
                  {selectedEvent.submitterName && (
                    <div>
                      <Text size="xs" color="dimmed" weight={600}>
                        Submitted By
                      </Text>
                      <Text size="sm">{selectedEvent.submitterName}</Text>
                    </div>
                  )}
                  <div>
                    <Text size="xs" color="dimmed" weight={600}>
                      Alert Delivery
                    </Text>
                    <Group spacing={6} mt={2}>
                      {selectedEvent.notifiedChannels?.whatsapp && (
                        <Badge size="xs" color="green" variant="light">
                          WhatsApp Sent
                        </Badge>
                      )}
                      {selectedEvent.notifiedChannels?.email && (
                        <Badge size="xs" color="blue" variant="light">
                          Email Sent
                        </Badge>
                      )}
                      {selectedEvent.notifiedChannels?.suppressedByCooldown && (
                        <Badge size="xs" color="gray" variant="outline">
                          Cooldown Active
                        </Badge>
                      )}
                      {!selectedEvent.notifiedChannels?.whatsapp &&
                        !selectedEvent.notifiedChannels?.email &&
                        !selectedEvent.notifiedChannels?.suppressedByCooldown && (
                          <Text size="xs" color="dimmed">
                            Logged in Dashboard
                          </Text>
                        )}
                    </Group>
                  </div>
                </SimpleGrid>
              </Paper>

              <div>
                <Text size="xs" weight={700} transform="uppercase" color="dimmed" mb={4}>
                  AI Contextual Analysis
                </Text>
                <Paper withBorder p="sm" radius="md">
                  <Text size="sm" style={{ lineHeight: 1.55 }}>
                    {selectedEvent.explanation}
                  </Text>
                </Paper>
              </div>

              {selectedEvent.recommendedAction && (
                <Alert
                  color="yellow"
                  title="Recommended Action"
                  icon={<AlertTriangle size={16} />}
                >
                  <Text size="sm">{selectedEvent.recommendedAction}</Text>
                </Alert>
              )}

              {Array.isArray(selectedEvent.quantitativeSignals) &&
                selectedEvent.quantitativeSignals.length > 0 && (
                  <div>
                    <Text
                      size="xs"
                      weight={700}
                      transform="uppercase"
                      color="dimmed"
                      mb={6}
                    >
                      Quantitative Telemetry Signals
                    </Text>
                    <Paper withBorder radius="sm">
                      <Table striped fontSize="xs">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Observed</th>
                            <th>Baseline</th>
                            <th>Deviation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedEvent.quantitativeSignals.map(
                            (sig: QuantitativeAnomalySignal, idx: number) => (
                              <tr key={`${sig.ruleId}-${idx}`}>
                                <td>
                                  <Text size="xs" weight={600}>
                                    {sig.metricLabel}
                                  </Text>
                                  <Text size="xs" color="dimmed">
                                    {sig.details}
                                  </Text>
                                </td>
                                <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                  {sig.observedValue}
                                  {sig.unit ? ` ${sig.unit}` : ""}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {sig.baselineValue !== undefined
                                    ? `${sig.baselineValue}${sig.unit ? ` ${sig.unit}` : ""}`
                                    : "—"}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {sig.ratioToBaseline !== undefined && (
                                    <Badge size="xs" color="red" variant="light" mr={4}>
                                      {sig.ratioToBaseline.toFixed(1)}x
                                    </Badge>
                                  )}
                                  {sig.zScore !== undefined && (
                                    <Badge size="xs" color="orange" variant="outline">
                                      {sig.zScore.toFixed(1)}σ
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </Paper>
                  </div>
                )}

              <Divider />

              <Group position="apart">
                <Group spacing="xs">
                  {selectedEvent.status !== "ACKNOWLEDGED" && (
                    <Button
                      size={controlSize}
                      style={{ minHeight: isMobile ? 44 : 36 }}
                      color="green"
                      variant="light"
                      leftIcon={<Check size={16} />}
                      loading={updatingId === selectedEvent.id}
                      onClick={() =>
                        handleStatusUpdate(selectedEvent.id, "ACKNOWLEDGED")
                      }
                    >
                      Mark Acknowledged
                    </Button>
                  )}
                  {selectedEvent.status !== "DISMISSED" && (
                    <Button
                      size={controlSize}
                      style={{ minHeight: isMobile ? 44 : 36 }}
                      color="gray"
                      variant="subtle"
                      leftIcon={<X size={16} />}
                      loading={updatingId === selectedEvent.id}
                      onClick={() =>
                        handleStatusUpdate(selectedEvent.id, "DISMISSED")
                      }
                    >
                      Dismiss
                    </Button>
                  )}
                  {selectedEvent.status !== "OPEN" && (
                    <Button
                      size={controlSize}
                      style={{ minHeight: isMobile ? 44 : 36 }}
                      color="red"
                      variant="subtle"
                      loading={updatingId === selectedEvent.id}
                      onClick={() => handleStatusUpdate(selectedEvent.id, "OPEN")}
                    >
                      Reopen
                    </Button>
                  )}
                </Group>

                <Button
                  size={controlSize}
                  style={{ minHeight: isMobile ? 44 : 36 }}
                  color="cap-blue"
                  rightIcon={<ArrowRight size={16} />}
                  onClick={() => {
                    const targetPath = buildSourceNavigationPath(selectedEvent);
                    setSelectedEventId(null);
                    navigate(targetPath);
                  }}
                >
                  View Source Record
                </Button>
              </Group>
            </Stack>
          </ScrollArea>
        )}
      </Drawer>
    </div>
  );
}

export default AnomaliesScreen;
